/**
 * Email + password sign-in. Google sign-in has been removed — the app is
 * now gated behind credentials stored in the users table.
 * On success: redirects to "/" if the password is OK, or to "/change-password"
 * if the user must rotate a temporary password.
 */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { isAuthenticated, user, refresh } = useAuth();
  const [, navigate] = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // If already signed in, bounce to the right place.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    navigate(user.mustChangePassword ? "/change-password" : "/");
  }, [isAuthenticated, user, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
        credentials: "include",
      });
      const body = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        mustChangePassword?: boolean;
        error?: string;
      };
      if (!res.ok || !body.success) {
        setError(body.error ?? "Sign-in failed");
        setSubmitting(false);
        return;
      }
      await refresh();
      navigate(body.mustChangePassword ? "/change-password" : "/");
    } catch (err) {
      console.error("[login] error:", err);
      setError("Network error — please try again.");
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-bold text-foreground">Footage Manager</h1>
          <p className="text-sm text-muted-foreground">Sign in to continue</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" disabled={submitting || !email || !password}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </Button>
        </form>

        <p className="text-xs text-muted-foreground text-center">
          Forgot your password? Contact your administrator for a reset.
        </p>
      </div>
    </div>
  );
}
